/**
 * ARTICLE D'EXEMPLE — généré le 2026-09-06 pour valider la maquette du blog.
 * À relire, compléter ou remplacer par du contenu réel avant/après mise en ligne.
 * Sources : 5 URL vérifiées automatiquement (4 OK), article révisé après vérification.
 * Format : voir src/blog/README.md
 */
export default {
  "slug": "micronora-2026-salon-microtechniques-acteurs-ia",
  "title": "Micronora 2026 : ce que le salon des microtechniques offre aux acteurs de l’IA",
  "excerpt": "Tous les deux ans, Micropolis accueille Micronora, le salon international des microtechniques. Tour d’horizon de ce qu’on y voit et de ce que l’IA peut y chercher.",
  "seoTitle": "Micronora 2026 : les microtechniques et l’IA",
  "seoDescription": "Du 29 septembre au 2 octobre 2026, Micropolis accueille le salon des microtechniques : robotique, vision industrielle et capteurs, ce que l’IA peut y chercher.",
  "category": "evenements",
  "tags": [
    "Micronora",
    "Microtechniques",
    "Salon",
    "Robotique",
    "Vision industrielle"
  ],
  "author": "jimmy",
  "publishedAt": "2026-08-21",
  "updatedAt": "2026-08-21",
  "featured": false,
  "cover": {
    "image": "/img/blog/besancon-le-hall-e-du-parc-des-expositions-micropolis.jpg",
    "credit": "Photo : le hall E du parc des expositions Micropolis, Besançon — Wikipedro, CC BY-SA 4.0, via Wikimedia Commons",
    "alt": "Allée d’un hall d’exposition industriel à Micropolis, avec des machines de micro-usinage et des bras robotisés en démonstration sur les stands."
  },
  "content": [
    {
      "type": "lead",
      "text": "Tous les deux ans, le parc des expositions Micropolis devient pendant quatre jours la vitrine de l’infiniment petit industriel. Micronora, salon international des microtechniques et de la précision, y réunit fabricants de machines, sous-traitants, laboratoires et donneurs d’ordre venus d’Europe et d’ailleurs. Pour qui travaille sur l’IA appliquée à l’industrie, c’est un terrain d’observation rare : capteurs, robots et données y sont à portée de main."
    },
    {
      "type": "keyfacts",
      "title": "En bref",
      "facts": [
        {
          "label": "Lieu",
          "value": "Parc des expositions Micropolis, Besançon"
        },
        {
          "label": "Rythme",
          "value": "Tous les deux ans, les années paires"
        },
        {
          "label": "Édition 2026",
          "value": "Du 29 septembre au 2 octobre"
        },
        {
          "label": "Envergure (édition 2024)",
          "value": "Plus de 800 exposants, dont un tiers venus de l’étranger"
        },
        {
          "label": "Fil rouge 2026",
          "value": "Zoom aéronautique, spatial et défense"
        }
      ]
    },
    {
      "type": "h2",
      "text": "Un salon né de l’horlogerie"
    },
    {
      "type": "p",
      "text": "Micronora est né au tournant des années 1970 pour promouvoir les techniques horlogères et la micromécanique bisontines. La crise horlogère a ensuite poussé le salon à se recentrer sur les techniques de précision en 1976, avant qu’il n’adopte le terme de « microtechniques » en 1980, selon l’historique publié par l’organisateur.[^1] Plus d’un demi-siècle plus tard, il se présente comme le salon européen de référence du secteur.[^1]"
    },
    {
      "type": "p",
      "text": "Cette longévité tient à l’écosystème qui l’entoure. Besançon a converti son savoir-faire horloger en une chaîne complète : [l’institut FEMTO-ST](/blog/femto-st-besancon-capitale-microtechniques), l’un des grands laboratoires français en sciences de l’ingénieur, avec des équipes en micro-nano-systèmes, optique, temps-fréquence et automatique[^3] ; SUPMICROTECH-ENSMM, école d’ingénieurs dédiée à la mécanique et aux microtechniques[^4] ; et [le technopole TEMIS](/blog/temis-technopole-startups-deeptech-besancon), qui regroupe entreprises et laboratoires sur un même site et se fait régulièrement l’écho du salon.[^2] Micronora est le moment où ces mondes se croisent dans les mêmes allées."
    },
    {
      "type": "h2",
      "text": "Ce que l’on voit dans les allées"
    },
    {
      "type": "p",
      "text": "Le périmètre du salon va de la recherche à la sous-traitance, en passant par les moyens de production. Concrètement, les stands couvrent :"
    },
    {
      "type": "ul",
      "items": [
        "le micro-usinage : machines-outils de haute précision, usinage laser, électroérosion, pour des pièces de quelques millimètres aux tolérances micrométriques ;",
        "la robotique et le micro-assemblage : bras, cobots, préhenseurs et systèmes de manipulation de composants minuscules ;",
        "l’optique et la photonique : lasers, systèmes de vision, composants optiques ;",
        "les capteurs et la microélectronique : MEMS, capteurs de force, de position ou de température, et l’électronique associée ;",
        "la métrologie et le contrôle : mesure dimensionnelle, tomographie, contrôle qualité en ligne ;",
        "la fabrication additive, les traitements de surface et les matériaux."
      ]
    },
    {
      "type": "p",
      "text": "Les marchés visés sont ceux qui exigent miniaturisation et fiabilité : dispositifs médicaux, aéronautique et spatial, horlogerie et luxe, automobile, énergie, télécommunications.[^1]"
    },
    {
      "type": "h2",
      "text": "Édition 2026 : le Zoom sur l’aéronautique, le spatial et la défense"
    },
    {
      "type": "p",
      "text": "Chaque édition consacre un espace thématique, le « Zoom », à un secteur ou à une technologie. En 2026, il est dédié à l’aéronautique, au spatial et à la défense, avec des démonstrations et un comité élargi à des professionnels de ces filières.[^1][^2] Autour de ce fil rouge, le salon garde ses rendez-vous habituels : [le concours des Microns d’Or](/blog/micronora-2024-microns-or-jumeau-numerique), qui distingue des innovations présentées par les exposants ; le Micro Nano Event, sessions de rendez-vous d’affaires internationaux ; des conférences ; et la Fabrique 4.0, une unité mobile de formation aux technologies de production.[^1]"
    },
    {
      "type": "p",
      "text": "L’édition 2024 avait réuni plus de 800 exposants et près de 15 000 visiteurs professionnels, avec un tiers d’exposants venus de l’étranger.[^2] L’ordre de grandeur annoncé pour 2026 est comparable."
    },
    {
      "type": "h2",
      "text": "Pourquoi les acteurs de l’IA devraient s’y intéresser"
    },
    {
      "type": "p",
      "text": "À première vue, Micronora n’est pas un salon du numérique : on y parle broches, tolérances et états de surface. C’est précisément ce qui le rend utile. L’IA industrielle ne manque pas de modèles ; elle manque de données propres, de cas d’usage précis et d’interlocuteurs qui possèdent les machines. Le salon réunit tout cela au même endroit."
    },
    {
      "type": "ul",
      "items": [
        "**Vision industrielle** : les caméras et systèmes optiques exposés sont les yeux des modèles de détection de défauts. Voir les contraintes réelles (éclairage, cadence, taille des pièces) évite bien des projets hors-sol.",
        "**Données de capteurs** : chaque machine de micro-usinage produit des séries temporelles (vibrations, températures, efforts), entrées naturelles de la maintenance prédictive et du contrôle de procédé.",
        "**Robotique** : la manipulation de micro-composants confronte la vision guidée par IA et l’apprentissage à des problèmes concrets, pas à des benchmarks.",
        "**IA embarquée** : les contraintes d’encombrement et de consommation des microsystèmes poussent vers des modèles compacts exécutés au plus près du capteur."
      ]
    },
    {
      "type": "p",
      "text": "Pour une équipe locale, c’est aussi le moyen le plus simple de rencontrer en une journée des industriels de la région, des chercheurs de FEMTO-ST et les acteurs du développement économique de l’agglomération.[^5]"
    },
    {
      "type": "callout",
      "tone": "tip",
      "title": "Pour préparer sa visite",
      "text": "Le salon est un rendez-vous professionnel (B2B) et se tient à Micropolis, à l’ouest de Besançon. Des navettes gratuites relient le parc des expositions à la gare Besançon Franche-Comté TGV pendant l’événement. Le catalogue des exposants est consultable sur le [site du salon](https://www.micronora.com/), ce qui permet de repérer à l’avance les stands liés à la vision, aux capteurs ou à la robotique.[^1]"
    },
    {
      "type": "h2",
      "text": "Et après ?"
    },
    {
      "type": "p",
      "text": "Micronora reste fidèle à son rythme bisannuel et à Besançon, mais l’organisateur prépare une déclinaison nord-américaine, Micronora Americas, annoncée au Palais des congrès de Montréal du 14 au 16 septembre 2027, selon TEMIS.[^6] C’est le signe qu’un salon né de l’horlogerie bisontine cherche désormais à s’exporter. Pour l’écosystème local, l’enjeu est plutôt de maintenir le lien entre ces machines de précision et les couches logicielles qui les rendent plus autonomes. L’édition 2026 dira où en est ce dialogue."
    }
  ],
  "references": [
    {
      "id": 1,
      "title": "Micronora – Salon international des microtechniques et de la précision",
      "publisher": "Micronora",
      "url": "https://www.micronora.com/",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 2,
      "title": "Micronora 2026 consacrera son Zoom à l’aéronautique-spatial-défense",
      "publisher": "TEMIS",
      "url": "https://www.temis.org/micronora-2026-consacrera-son-zoom-a-laeronautique-spatial-defense/",
      "type": "official",
      "date": "2026-05-07"
    },
    {
      "id": 3,
      "title": "Institut FEMTO-ST",
      "publisher": "FEMTO-ST / CNRS",
      "url": "https://www.femto-st.fr/",
      "type": "academic",
      "date": "2026-09-06"
    },
    {
      "id": 4,
      "title": "SUPMICROTECH-ENSMM – École nationale supérieure de mécanique et des microtechniques",
      "publisher": "SUPMICROTECH-ENSMM",
      "url": "https://www.supmicrotech.fr/",
      "type": "academic",
      "date": "2026-09-06"
    },
    {
      "id": 5,
      "title": "Grand Besançon Métropole",
      "publisher": "Grand Besançon Métropole",
      "url": "https://www.grandbesancon.fr/",
      "type": "official",
      "date": "2026-09-06"
    },
    {
      "id": 6,
      "title": "Le salon bisontin Micronora s’internationalise et prévoit de s’exporter au Canada en 2027",
      "publisher": "TEMIS",
      "url": "https://www.temis.org/le-salon-bisontin-micronora-sinternationalise-et-prevoit-de-sexporter-au-canada-en-2027/",
      "type": "official",
      "date": "2026-04-30"
    }
  ]
};
